import React, { useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    Paper,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
    Card,
    CardContent,
    Link,
    Button,
} from '@mui/material';

import {
    Group as GroupIcon,
    Checklist as ChecklistIcon,
    Paid as PaidIcon,
    Gavel as GavelIcon,
    PsychologyAlt as PsychologyAltIcon,
    CheckCircleOutline as CheckCircleOutlineIcon,
    Info as InfoIcon,
    Home as HomeIcon,
    EmojiPeople as EmojiPeopleIcon,
    Restaurant as RestaurantIcon,
    AccessTime as AccessTimeIcon,
    Security as SecurityIcon,
    EmojiEmotions as EmojiEmotionsIcon,
    FilterList as FilterListIcon,
    ArrowForwardIos as ArrowForwardIosIcon,
    Replay as ReplayIcon,
    Close as CloseIcon,
} from '@mui/icons-material';

// スクロール用の関数
const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
        const offset = 80; // ヘッダーの高さ分のオフセット
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;

        window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
        });
    }
};

const tocItems = [
    { id: 'introduction', label: '導入：ルームシェア成功のカギは「相手選び」', icon: <GroupIcon fontSize="small" /> },
    { id: 'criteria', label: '1. ルームシェアパートナー選びの基準', icon: <FilterListIcon fontSize="small" /> },
    { id: 'checklist', label: '2. 生活習慣チェックリスト', icon: <ChecklistIcon fontSize="small" /> },
    { id: 'money', label: '3. 金銭面の取り決め', icon: <PaidIcon fontSize="small" /> },
    { id: 'rules', label: '4. トラブル防止のためのルールづくり', icon: <GavelIcon fontSize="small" /> },
    { id: 'compatibility', label: '5. 相性診断で見極めるポイント', icon: <PsychologyAltIcon fontSize="small" /> },
    { id: 'summary', label: 'まとめ：理想のルームシェアをはじめよう', icon: <EmojiEmotionsIcon fontSize="small" /> },
];

const PostContent = () => {
    const [showToc, setShowToc] = useState(true);
    return (
        <Box sx={{ maxWidth: 800, mx: 'auto', px: 3 }}>
            {showToc ? (
                <Card sx={{ mb: 4, boxShadow: 2, bgcolor: '#e3f2fd', border: '1px solid #ddd', borderRadius: 2 }}>
                    <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="h6" sx={{ flexGrow: 1 }}>目次</Typography>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<CloseIcon />}
                                onClick={() => setShowToc(false)}
                            >
                                閉じる
                            </Button>
                        </Box>
                        <List sx={{ p: 0 }}>
                            {tocItems.map((item, idx) => (
                                <ListItem key={item.id} sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                    <Box sx={{
                                        minWidth: 32,
                                        minHeight: 32,
                                        bgcolor: '#f5f5f5',
                                        color: '#888',
                                        fontWeight: 'bold',
                                        fontSize: '1.15rem',
                                        borderRadius: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mr: 1
                                    }}>
                                        {idx + 1}.
                                    </Box>
                                    <Link
                                        component="button"
                                        onClick={() => {
                                            scrollToSection(item.id);
                                            setShowToc(false);
                                        }}
                                        color="inherit"
                                        underline="hover"
                                        sx={{ cursor: 'pointer', fontSize: '0.95rem', display: 'flex', alignItems: 'center' }}
                                    >
                                        {item.icon}
                                        <Box sx={{ ml: 1 }}>{item.label}</Box>
                                    </Link>
                                </ListItem>
                            ))}
                        </List>
                    </CardContent>
                </Card>
            ) : (
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setShowToc(true)}
                    sx={{ mb: 2 }}
                    startIcon={<ChecklistIcon />}
                >
                    目次を表示
                </Button>
            )}

            {/* 導入 */}
            <Box id="introduction" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    導入：ルームシェア成功のカギは「相手選び」
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    近年、家賃や生活費の節約だけでなく、「新たな人間関係」や「豊かな日常」を求めてルームシェアを始める方が増えています。しかし、<b>ルームシェア最大の難関は「相手選び」</b>。仲良しの友人とでも、生活スタイルや価値観がズレていると小さな違和感が積み重なり、最悪の場合トラブルに発展することも…。
                </Typography>
                <Box sx={{ my: 3 }}>

                    <Typography variant="caption" sx={{ color: '#666', display: 'block', textAlign: 'center' }}>
                        居心地の良い共有リビングでは、信頼できるルームメイト同士の関係づくりが大切
                    </Typography>
                </Box>
                <Typography variant="body1">
                    この記事では、<b>ルームシェアの専門家が「失敗しない相手の選び方と必須の事前確認ポイント5選」</b>を徹底解説！
                    これからルームシェアを始めたい方や、今まさに相手探し中の方は、ぜひチェックしてみてください。
                </Typography>
            </Box>

            {/* 1. ルームシェアパートナー選びの基準 */}
            <Box id="criteria" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    1. ルームシェアパートナー選びの基準
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={7}>
                        <Typography variant="body1">
                            ルームシェアにおいて「誰と住むか」は最重要事項です。選ぶ基準をクリアにしておくことで、後悔のないパートナー選びができます。<br />
                            ルームシェア向きの人物像を、<b>5つの視点</b>から整理しました。
                        </Typography>
                        <List sx={{ pl: 1, mt: 1 }}>
                            <ListItem>
                                <ListItemIcon><EmojiPeopleIcon color="primary" /></ListItemIcon>
                                <ListItemText
                                    primary={<b>1. コミュニケーション力</b>}
                                    secondary="適度な距離感で会話や相談ができる人。"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon><HomeIcon color="primary" /></ListItemIcon>
                                <ListItemText
                                    primary={<b>2. 清潔感や整理整頓力</b>}
                                    secondary="共用スペースをきれいに使えるか。"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon><RestaurantIcon color="primary" /></ListItemIcon>
                                <ListItemText
                                    primary={<b>3. 生活リズムの近さ</b>}
                                    secondary="就寝・起床・食事などの時間帯が大きくズレていないか。"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon><AccessTimeIcon color="primary" /></ListItemIcon>
                                <ListItemText
                                    primary={<b>4. 時間や約束を守れる</b>}
                                    secondary="ルールや時間を守る誠実な性格かどうか。"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon><SecurityIcon color="primary" /></ListItemIcon>
                                <ListItemText
                                    primary={<b>5. 金銭感覚のバランス</b>}
                                    secondary="家賃・光熱費・共有費の分担意識があるか。"
                                />
                            </ListItem>
                        </List>
                        <Typography variant="body2" sx={{ mt: 2 }}>
                            <InfoIcon sx={{ verticalAlign: 'middle', color: 'primary.main', mr: 0.5 }} />
                            <b>ワンポイント：</b>
                            <span>「一緒にいて楽しい」だけでなく、生活の実務面も冷静にチェックしましょう。</span>
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={5}>
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>

                        </Box>
                    </Grid>
                </Grid>
            </Box>

            {/* 2. 生活習慣チェックリスト */}
            <Box id="checklist" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    2. 生活習慣チェックリスト
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    パートナー選びで最も重要なのは「生活リズムや習慣の相性」です。以下のチェックリストで、お互いの生活スタイルを事前に確認しましょう。
                </Typography>
                <Paper elevation={2} sx={{ p: 2, borderRadius: 2, bgcolor: '#fafafa', mb: 2 }}>
                    <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>
                        生活習慣チェックリスト
                    </Typography>
                    <List dense>
                        <ListItem>
                            <ListItemIcon><CheckCircleOutlineIcon color="success" /></ListItemIcon>
                            <ListItemText primary="起床・就寝時間は似ているか" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><CheckCircleOutlineIcon color="success" /></ListItemIcon>
                            <ListItemText primary="平日と休日の過ごし方" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><CheckCircleOutlineIcon color="success" /></ListItemIcon>
                            <ListItemText primary="自炊・外食の頻度" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><CheckCircleOutlineIcon color="success" /></ListItemIcon>
                            <ListItemText primary="シャワーやお風呂の時間帯" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><CheckCircleOutlineIcon color="success" /></ListItemIcon>
                            <ListItemText primary="掃除・洗濯の頻度や方法" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><CheckCircleOutlineIcon color="success" /></ListItemIcon>
                            <ListItemText primary="友人を家に呼ぶ頻度" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><CheckCircleOutlineIcon color="success" /></ListItemIcon>
                            <ListItemText primary="音楽・テレビの音量や時間帯" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><CheckCircleOutlineIcon color="success" /></ListItemIcon>
                            <ListItemText primary="ペットの有無・アレルギー" />
                        </ListItem>
                    </List>
                    <Typography variant="body2" sx={{ color: '#666', mt: 1 }}>
                        できるだけ具体的に話し合い、違いがあれば妥協点を探しましょう。
                    </Typography>
                </Paper>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <Chip label="生活リズム" color="primary" variant="outlined" sx={{ mr: 1, mb: 1 }} />
                        <Chip label="掃除・家事" color="primary" variant="outlined" sx={{ mr: 1, mb: 1 }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Chip label="交友関係" color="primary" variant="outlined" sx={{ mr: 1, mb: 1 }} />
                        <Chip label="ペット・アレルギー" color="primary" variant="outlined" sx={{ mr: 1, mb: 1 }} />
                    </Grid>
                </Grid>
            </Box>

            {/* 3. 金銭面の取り決め */}
            <Box id="money" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    3. 金銭面の取り決め
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    金銭トラブルはルームシェア解消理由のトップ。<b>最初に「お金のルール」を明確に</b>しておくことが必須です。
                </Typography>
                <Paper elevation={1} sx={{ p: 2, borderRadius: 2, mb: 2 }}>
                    <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>
                        金銭面の主な取り決め項目
                    </Typography>
                    <List dense>
                        <ListItem>
                            <ListItemIcon><PaidIcon color="primary" /></ListItemIcon>
                            <ListItemText primary="家賃・光熱費の精算方法（銀行振込/現金/アプリ等）" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><PaidIcon color="primary" /></ListItemIcon>
                            <ListItemText primary="共有日用品（洗剤・トイレットペーパー等）の負担ルール" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><PaidIcon color="primary" /></ListItemIcon>
                            <ListItemText primary="家電・家具の購入/修理・廃棄時の分担" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><PaidIcon color="primary" /></ListItemIcon>
                            <ListItemText primary="突発的な支出（修繕費、引越し費用等）の対応" />
                        </ListItem>
                    </List>
                    <Typography variant="body2" sx={{ color: '#666', mt: 1 }}>
                        <InfoIcon sx={{ verticalAlign: 'middle', color: 'primary.main', mr: 0.5 }} />
                        <b>家賃の振込日や精算方法は必ず書面やチャットで記録しましょう。</b>
                    </Typography>
                </Paper>
            </Box>

            {/* 4. トラブル防止のためのルールづくり */}
            <Box id="rules" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    4. トラブル防止のためのルールづくり
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    「なんとなく」で始めると後悔しがち。<b>ルールはできるだけ明文化</b>しておくのがおすすめです。
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={7}>
                        <List>
                            <ListItem>
                                <ListItemIcon><GavelIcon color="primary" /></ListItemIcon>
                                <ListItemText
                                    primary="共有スペースの利用ルール"
                                    secondary="リビング・キッチン・バスルームの使い方や掃除当番"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon><GavelIcon color="primary" /></ListItemIcon>
                                <ListItemText
                                    primary="来客・友人の宿泊に関するルール"
                                    secondary="事前連絡の有無や頻度の上限設定"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon><GavelIcon color="primary" /></ListItemIcon>
                                <ListItemText
                                    primary="ゴミ出し・共有物の管理"
                                    secondary="当番制やルールブックの作成"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon><GavelIcon color="primary" /></ListItemIcon>
                                <ListItemText
                                    primary="生活音・音楽・家電の利用時間"
                                    secondary="深夜・早朝の配慮やイヤホンの活用"
                                />
                            </ListItem>
                        </List>
                        <Typography variant="body2" sx={{ color: '#666', mt: 1 }}>
                            <InfoIcon sx={{ verticalAlign: 'middle', color: 'primary.main', mr: 0.5 }} />
                            <b>書面化やグループチャットでの合意記録</b>を残しておくと、揉めごと防止に有効です。
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={5}>
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>

                        </Box>
                    </Grid>
                </Grid>
            </Box>

            {/* 5. 相性診断で見極めるポイント */}
            <Box id="compatibility" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    5. 相性診断で見極めるポイント
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    最後は「感覚」も大切。以下の簡易相性テストでパートナーとの相性度をセルフチェックしてみましょう。
                </Typography>
                <Paper elevation={2} sx={{ p: 2, borderRadius: 2, bgcolor: '#f1f8e9', mb: 2 }}>
                    <Typography variant="h6" sx={{ color: 'success.main', mb: 1 }}>
                        ルームシェア相性診断テスト
                    </Typography>
                    <List dense>
                        <ListItem>
                            <ListItemIcon><PsychologyAltIcon color="primary" /></ListItemIcon>
                            <ListItemText primary="沈黙が気まずくない相手か" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><PsychologyAltIcon color="primary" /></ListItemIcon>
                            <ListItemText primary="些細な要望を言いやすい雰囲気があるか" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><PsychologyAltIcon color="primary" /></ListItemIcon>
                            <ListItemText primary="相手の欠点を受け入れられるか" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><PsychologyAltIcon color="primary" /></ListItemIcon>
                            <ListItemText primary="「ありがとう」「ごめん」が素直に言えるか" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><PsychologyAltIcon color="primary" /></ListItemIcon>
                            <ListItemText primary="金銭感覚や価値観の違いを楽しめるか" />
                        </ListItem>
                    </List>
                    <Typography variant="body2" sx={{ color: '#666', mt: 1 }}>
                        <InfoIcon sx={{ verticalAlign: 'middle', color: 'success.main', mr: 0.5 }} />
                        3つ以上「YES」なら安心。逆に不安がある場合は、じっくり話し合いを重ねましょう。
                    </Typography>
                </Paper>
                <Box sx={{ my: 2 }}>
                    <img
                        src="/images/blog/roommates-chat_hajimete-no-roomshare-shippai-shinai-aite-no-erabikata-to-jizen-kakunin-point-10sen.jpg"
                        alt="窓際のソファで2人のルームメイトがリラックスして談笑し、互いの価値観や相性について穏やかに話し合っている様子。柔らかい自然光と和やかな雰囲気が特徴。"
                        style={{ width: '100%', maxWidth: 400, borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.07)' }}
                    />
                    <Typography variant="caption" sx={{ color: '#666', display: 'block', textAlign: 'center' }}>
                        価値観や相性の確認も、穏やかな雰囲気でリラックスしながら話すのがおすすめ
                    </Typography>
                </Box>
            </Box>

            {/* まとめ */}
            <Box id="summary" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    まとめ：理想のルームシェアをはじめよう
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェアは、<b>生活を豊かにし、人間関係を広げる絶好のチャンス</b>。ですが、成功の秘訣は「信頼できる相手選び」と「事前の確認」です。
                    <br />
                    <br />
                    <b>今回紹介した5のポイント</b>を参考に、<span style={{ color: '#1976d2' }}>納得と安心のルームシェア</span>をスタートしてください。
                </Typography>
                <Paper elevation={2} sx={{ p: 2, borderRadius: 2, bgcolor: '#fff3e0' }}>
                    <Typography variant="body2" sx={{ color: '#ef6c00' }}>
                        <EmojiEmotionsIcon sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                        <b>アドバイス：</b>迷った時や不安な点は、第三者やルームシェア経験者に相談するのも一つの方法です。
                    </Typography>
                </Paper>
            </Box>
        </Box>
    );
};

const post = {
    id: 'hajimete-no-roomshare-shippai-shinai-aite-no-erabikata-to-jizen-kakunin-point-10sen',
    slug: 'hajimete-no-roomshare-shippai-shinai-aite-no-erabikata-to-jizen-kakunin-point-10sen',
    title: '初めてのルームシェア！失敗しない相手の選び方と事前確認ポイント5選',
    excerpt: 'パートナー選びの基準\n生活習慣チェックリスト\n金銭面の取り決め\nトラブル防止のルール\n相性診断テスト',
    coverImage: '/images/blog/cover-living-dining_hajimete-no-roomshare-shippai-shinai-aite-no-erabikata-to-jizen-kakunin-point-10sen.jpg',
    coverImageAlt: '明るい自然光が差し込むモダンなルームシェアの共有リビングで、多様な背景を持つルームメイトたちが楽しく会話をしている様子。ナチュラルなインテリアとリラックスした雰囲気が特徴で、協力的な共同生活の魅力を象徴している。',
    publishedAt: new Date().toISOString(),
    isPublished: true,
    categories: ['ルームシェア', '住まい', '生活の知恵'],
    tags: ['ルームシェア', 'パートナー選び', '生活習慣', 'トラブル防止', '相性診断'],
    author: {
        name: '松本理恵',
        avatar: '/images/avatars/matsumoto-rie.jpg',
        bio: 'シェアハウス運営歴10年以上。住まいと人間関係の両立をテーマに、全国でルームシェアのアドバイスやコンサルティングを行う。',
        profilePicture: '/images/avatars/matsumoto-rie.jpg'
    },
    viewCount: 1240,
    contentComponent: PostContent
};

export default post;